import { EntityId } from '@reduxjs/toolkit';
import { Logger, LogType } from '@hitiq/logger';
import { Subscription } from 'rxjs';
import { Result } from '@hitiq/generic';
import { store } from '../store';
import { Persistance } from '../dataStorePersistence/persistence';
import { AmplifyDataStore } from '../dataStorePersistence/AmplifyDataStore';
import {
  DataStoreModel,
  ModelTypes,
} from '../dataStorePersistence/DataStoreModel';
import { Dispatchers } from './dispatchers.interface';
import { Selectors } from './selectors.interface';
import { Translator } from './translator.interface';
// import { AuthDataService } from '../helperServices/auth/auth.data.service';
import { useAppSelector } from '../hooks';
import { User } from './users/users.interfaces';

// A Repository gives you access to redux with your choice of persistence. The persistence should be completely encapsulated inside this class. Maybe this should be called ReduxRepo.
// TODO: create a rxjsRepo.
// TODO: create a CRUDRepo.
export class ReduxRepository<
  Model extends DataStoreModel,
  BackendModel extends DataStoreModel,
> {
  protected user?: User;
  private onCreateSub: Subscription | undefined;
  private onUpdateSub: Subscription | undefined;
  private onDeleteSub: Subscription | undefined;
  protected persistence: Persistance<BackendModel>;

  constructor(
    protected readonly modelType: ModelTypes,
    protected readonly translator: Translator<BackendModel, Model>,
    // TODO: make interface when we have more then one.
    protected readonly datastore: AmplifyDataStore<BackendModel>,
    protected readonly loggingService: Logger | null,
    // protected authDataService: AuthDataService,
    // TODO: group these as slice
    public readonly selectors: Selectors<Model>,
    // TODO: figure out how to get the entityAdapter.action types
    public readonly dispatchers: Dispatchers<Model>,
    public readonly createThunk: any,
    public readonly updateThunk: any,
    public readonly deleteThunk: any,
    public readonly loadThunk: any,
  ) {
    this.persistence = new Persistance<BackendModel>(
      this.datastore,
      this.modelType,
    );
  }

  useResourcesSelector = () => {
    return useAppSelector(this.selectors.resourcesSelector);
  };

  useIsLoadingSelector = () => {
    return useAppSelector(this.selectors.isLoadingSelector);
  };

  useErrorsSelector = () => {
    return useAppSelector(this.selectors.errorsSelector);
  };

  getResource(id: string) {
    return this.selectors
      .resourcesSelector(store.getState())
      .find((resource) => resource.id === id);
  }

  getResources() {
    return this.selectors.resourcesSelector(store.getState());
  }

  setUpRepository(user: User): void {
    this.user = user;

    // TODO: make sure these are only subscribed to once and that they are unsubscribed to.
    this.subscribeToOnCreate();
    this.subscribeToOnUpdate();
    this.subscribeToOnDelete();
  }

  dispatchLoad(): void {
    store.dispatch(
      this.loadThunk({
        persistence: this.persistence,
        loggingService: this.loggingService,
        translator: this.translator,
      }),
    );
  }

  dispatchCreate(resource: Partial<Model>, extra?: any): void {
    if (!this.user) {
      console.error('should never get here. Need to set a user');
      return;
    }

    const resourceWithOwner = {
      ...resource,
      // TODO: make consistent with backend for all resources
      // QUESTION: do we need this frontend for anything if user stays offline while doing other things?
      accountId: this.user.accountId,
      accountType: this.user.accountType,
      createdByUserId: this.user.id,
    };

    store.dispatch(
      this.createThunk({
        translator: this.translator,
        persistence: this.persistence,
        loggingService: this.loggingService,
        resource: resourceWithOwner,
        // TODO: add only to conneqt or rename
        existingConneqts: this.getResources(),
        extra,
      }),
    );
  }

  dispatchCreateAsync(resource: Partial<Model>, extra?: any): Promise<string> {
    if (!this.user) {
      // TODO: TEST THIS SO IT DOESN'T FAIL IN DOMAIN SERVICE
      throw new Error('should never get here. Need to set a user');
      // return;
    }

    const resourceWithOwner = {
      ...resource,
      // TODO: make consistent with backend for all resources
      // QUESTION: do we need this frontend for anything if user stays offline while doing other things?
      accountId: this.user.accountId,
      accountType: this.user.accountType,
      createdByUserId: this.user.id,
    };

    return store
      .dispatch(
        this.createThunk({
          translator: this.translator,
          persistence: this.persistence,
          loggingService: this.loggingService,
          resource: resourceWithOwner,
          extra,
        }),
      )
      .unwrap();
  }

  dispatchUpdate(resourceChange: Partial<Model> & Pick<Model, 'id'>): void {
    if (!this.user) {
      console.error('should never get here. Need to set a user');
      return;
    }
    const resourceWithOwner = {
      ...resourceChange,
      // accountId: this.user.accountId,
      // accountType: this.user.accountType,
      // createdByUserId: this.user.id,
    };

    store.dispatch(
      this.updateThunk({
        translator: this.translator,
        persistence: this.persistence,
        resourceChange: resourceWithOwner,
      }),
    );
  }

  // TODO: send it id only and use this.modelType for type in persistence. No need to translateOneToBackendModel because no other properties are used in persistence.delete.
  dispatchDelete(id: string): void {
    store.dispatch(
      this.deleteThunk({
        persistence: this.persistence,
        dataStoreModel: {
          id,
          modelType: this.modelType,
        },
      }),
    );
  }

  dispatchClearError(id: string) {
    store.dispatch(this.dispatchers.clearError(id));
  }

  private dispatchLoading() {
    store.dispatch(this.dispatchers.loading());
  }

  private handleResult = (result: Result<BackendModel>) => {
    if (!result.success) {
      this.dispatchAddError(result.error);
    }

    this.loggingService?.log(
      LogType.info,
      `${this.modelType} - ${JSON.stringify(result.data)}`,
    );
  };

  private dispatchAddError = (error: Error) => {
    this.loggingService?.log(
      LogType.error,
      `${this.modelType} - ${error.message}`,
    );

    // if (this.dispatchers.addErrors) {
    //   // TODO: format error to be an alert if you want to display it to user.
    // store.dispatch(this.dispatchers.addErrors([error]));
    // } else {
    // this.loggingService?.log(
    //   LogType.error,
    //   `${this.modelType} - 'You have to send in this.dispatchers.addErrors to be able access errors'`,
    // );
    // }
  };

  private subscribeToOnCreate() {
    if (this.dispatchers.addResource) {
      if (this.onCreateSub) {
        this.onCreateSub.unsubscribe();
      }
      this.onCreateSub = this.persistence.onCreated.subscribe(
        (backendResource) => {
          if (!this.user) {
            console.error('should never get here. Need to set a user');
            return;
          }
          if (
            // TODO need to recheck this logic
            this.user.accountId !== backendResource.accountId &&
            this.user.accountId !== backendResource.responseAccountId
          ) {
            return;
          }
          // TODO: figure out why no translation is needed?
          const resource = this.translator.translateOne(backendResource);

          store.dispatch(this.dispatchers.addResource(resource));
        },
      );
    }
  }

  private subscribeToOnUpdate() {
    if (this.dispatchers.updateResource) {
      if (this.onUpdateSub) {
        this.onUpdateSub.unsubscribe();
      }

      this.onUpdateSub = this.persistence.onUpdated.subscribe(
        (backendResource) => {
          if (!this.user) {
            console.error('should never get here. Need to set a user');
            return;
          }
          // TODO need to recheck this logic
          if (
            this.user.accountId !== backendResource.accountId &&
            this.user.accountId !== backendResource.responseAccountId
          ) {
            return;
          }
          // TODO: figure out if translation is needed?
          const resource = this.translator.translateOne(backendResource);
          // QUESTION: what is the point of using changes if we always add the whole resource not just changes?
          // store.dispatch(this.dispatchers.upsertOne(resource));
          store.dispatch(
            this.dispatchers.updateResource({
              id: resource.id as EntityId,
              changes: resource,
            }),
          );
        },
      );
    }
  }
  private subscribeToOnDelete() {
    if (this.dispatchers.deleteResource) {
      if (this.onDeleteSub) {
        this.onDeleteSub.unsubscribe();
      }
      this.onDeleteSub = this.persistence.onDeleted.subscribe(
        (backendResource) => {
          // console.log('onDeleted: ', backendResource);
          // const resource = this.translator.translateOne(backendResource);
          // TODO: figure out if translation is needed?
          store.dispatch(
            this.dispatchers.deleteResource(backendResource.id as EntityId),
          );
        },
      );
    }
  }

  find(resources: Model[], id: string | undefined): Model | undefined {
    return resources.find((resource) => resource.id === id);
  }

  async createAsync(resource: Model): Promise<Model> {
    const backendResource =
      this.translator.translateOneToBackendModel(resource);

    const result = await this.persistence.create(backendResource);

    if (result.success) {
      return this.translator.translateOne(result.data);
    }
    throw result.error;
  }
}
